import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import BillingSection from './Settings';

const initialMessage = {
  sender: 'GrokCoder',
  text: "Hey! I'm GrokCoder, your autonomous developer agent. My code lives in /grok-chat and /root/grok-agent. What feature should we build or bug fix next? 🚀"
};

const TOOL_ICONS = {
  write_file: 'fa-pen',
  read_file: 'fa-file-alt',
  list_directory: 'fa-folder-open',
  run_command: 'fa-terminal',
  deploy_frontend: 'fa-rocket',
  restart_backend: 'fa-redo',
  git_sync: 'fa-code-branch',
  generate_image: 'fa-image',
  web_search: 'fa-search',
  fetch_url: 'fa-globe',
  default: 'fa-cog',
};

function ToolStep({ step }) {
  const [expanded, setExpanded] = useState(false);
  const isDone = step.result !== undefined;
  const isError = step.error;
  const icon = TOOL_ICONS[step.tool] || TOOL_ICONS.default;

  return (
    <div className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
      isError ? 'border-red-500/30 bg-red-500/5' :
      isDone ? 'border-[#2a2a2a]/60 bg-[#111]/60' :
      'border-emerald-500/30 bg-emerald-500/5'
    }`} >
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
        onClick={() => isDone && setExpanded(e => !e)}
      >
        <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${
          isError ? 'text-red-400' : isDone ? 'text-gray-400' : 'text-emerald-400'
        }`} >
          {!isDone ? (
            <span className="inline-block w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          ) : isError ? (
            <i className="fas fa-exclamation-circle text-sm" />
          ) : (
            <i className="fas fa-check-circle text-sm" />
          )}
        </span>
        <i className={`fas ${icon} text-xs flex-shrink-0 ${isDone ? 'text-gray-500' : 'text-emerald-300'}`} />
        <span className={`text-sm font-mono flex-1 truncate ${isDone ? 'text-gray-300' : 'text-emerald-200'}`} >
          {step.label}
        </span>
        {isDone && (
          <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-xs text-gray-600 flex-shrink-0`} />
        )}
      </button>
      {expanded && isDone && step.result && (
        <div className="px-4 pb-3 border-t border-[#2a2a2a]/40">
          <pre className="text-xs text-gray-400 whitespace-pre-wrap break-words mt-2 max-h-48 overflow-y-auto font-mono leading-relaxed">
            {step.result}
          </pre>
        </div>
      )}
    </div>
  );
}

function ActivityBubble({ steps, statusText }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] w-full px-4 py-4 rounded-3xl bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#333]/60 shadow-lg shadow-black/30">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-[#2a2a2a]/60">
          <span className="inline-block w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <span className="text-sm text-emerald-300 font-medium">{statusText}</span>
        </div>
        <div className="space-y-2">
          {steps.map((step, i) => (
            <ToolStep key={step.id || i} step={step} />
          ))}
        </div>
      </div>
    </div>
  );
}

function parseMessageContent(text) {
  try {
    const parsed = JSON.parse(text);
    if (parsed.type === 'image' && parsed.url) return { type: 'image', url: parsed.url, prompt: parsed.prompt };
  } catch (e) {}
  return { type: 'text', text };
}

function CompletedActivity({ msg }) {
  const [showSteps, setShowSteps] = useState(false);
  const completedSteps = (msg.steps || []).filter(s => s.result !== undefined);
  const content = parseMessageContent(msg.text);

  const bubbleClass = 'max-w-full px-6 py-4 rounded-3xl text-base/6 leading-relaxed shadow-lg shadow-black/30 whitespace-pre-wrap break-words select-text bg-[#1a1a1a]/80 backdrop-blur-sm text-gray-100/95 border border-[#333]/60';

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] w-full flex flex-col gap-2">
        {completedSteps.length > 0 && (
          <div className="bg-[#111]/60 border border-[#2a2a2a]/60 rounded-2xl overflow-hidden">
            <button
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/5 transition-colors"
              onClick={() => setShowSteps(e => !e)}
            >
              <i className="fas fa-check-circle text-xs text-emerald-400" />
              <span className="text-xs text-gray-400 flex-1">
                {completedSteps.length} action{completedSteps.length !== 1 ? 's' : ''} taken
              </span>
              <i className={`fas fa-chevron-${showSteps ? 'up' : 'down'} text-xs text-gray-600`} />
            </button>
            {showSteps && (
              <div className="px-3 pb-3 space-y-2 border-t border-[#2a2a2a]/40 pt-2">
                {completedSteps.map((step, i) => <ToolStep key={step.id || i} step={step} />)}
              </div>
            )}
          </div>
        )}
        {content.type === 'image' ? (
          <div className={bubbleClass.replace('whitespace-pre-wrap break-words', '')}>
            <img src={content.url} alt={content.prompt || 'Generated image'} className="rounded-2xl max-w-full max-h-96 object-contain" />
            {content.prompt && <p className="text-xs text-gray-500 mt-2 italic">{content.prompt}</p>}
          </div>
        ) : (
          <div className={bubbleClass}>{content.text}</div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.sender === 'You';

  if (msg.steps !== undefined) {
    return <CompletedActivity msg={msg} />;
  }

  const content = parseMessageContent(msg.text);
  const bubbleClass = isUser
    ? 'max-w-[85%] px-6 py-4 rounded-3xl text-base/6 leading-relaxed shadow-lg shadow-black/30 whitespace-pre-wrap break-words select-text bg-gradient-to-br from-indigo-500/20 via-blue-500/20 to-purple-500/20 text-white border border-indigo-500/40 ml-auto'
    : 'max-w-[85%] px-6 py-4 rounded-3xl text-base/6 leading-relaxed shadow-lg shadow-black/30 whitespace-pre-wrap break-words select-text bg-[#1a1a1a]/80 backdrop-blur-sm text-gray-100/95 border border-[#333]/60';

  if (content.type === 'image') {
    return (
      <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
        <div className={bubbleClass.replace('whitespace-pre-wrap break-words', '')}>
          <img src={content.url} alt={content.prompt || 'Generated image'} className="rounded-2xl max-w-full max-h-96 object-contain" />
          {content.prompt && <p className="text-xs text-gray-500 mt-2 italic">{content.prompt}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
      <div className={bubbleClass}>{content.text}</div>
    </div>
  );
}

function App() {
  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('grok-4-1-fast-reasoning');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [pendingImage, setPendingImage] = useState(null);
  const [version, setVersion] = useState('...');
  const [liveSteps, setLiveSteps] = useState([]);
  const [liveStatus, setLiveStatus] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [gitKey, setGitKey] = useState('');
  const [sshKey, setSshKey] = useState('');
  const [gitRepoPath, setGitRepoPath] = useState('');
  const [sshServerPath, setSshServerPath] = useState('');
  const bottomRef = useRef(null);
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get('/api/version').then(r => setVersion(r.data.version)).catch(() => setVersion('?'));
    axios.get('/api/changelog').then(r => setChangelogEntries(r.data.entries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setGitKey(localStorage.getItem('gitKey') || '');
    setSshKey(localStorage.getItem('sshKey') || '');
    setGitRepoPath(localStorage.getItem('gitRepoPath') || '');
    setSshServerPath(localStorage.getItem('sshServerPath') || '');
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, liveSteps]);

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.focus();
  }, []);

  const adjustHeight = () => {
    const ta = textAreaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPendingImage({ base64: dataUrl.split(',')[1], mimeType: file.type, previewUrl: dataUrl });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const saveSettings = () => {
    localStorage.setItem('gitKey', gitKey);
    localStorage.setItem('sshKey', sshKey);
    localStorage.setItem('gitRepoPath', gitRepoPath);
    localStorage.setItem('sshServerPath', sshServerPath);
    setShowSettings(false);
  };

  const sendMessage = async () => {
    if ((!input.trim() && !pendingImage) || loading) return;

    let userContent;
    if (pendingImage) {
      userContent = [
        ...(input.trim() ? [{ type: 'text', text: input.trim() }] : []),
        { type: 'image_url', image_url: { url: `data:${pendingImage.mimeType};base64,${pendingImage.base64}` } }
      ];
    } else {
      userContent = input.trim();
    }

    const userMsg = { sender: 'You', text: input.trim() || '📎 Image attached', imagePreview: pendingImage?.previewUrl || null };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingImage(null);
    adjustHeight();
    setLoading(true);
    setLiveSteps([]);
    setLiveStatus('Thinking...');

    const history = [...messages, { sender: 'You', text: input.trim() || '📎 Image attached' }];
    const apiMessages = history.map((m, i) => {
      if (i === history.length - 1 && m.sender === 'You' && userContent !== input.trim()) {
        return { role: 'user', content: userContent };
      }
      return { role: m.sender === 'You' ? 'user' : 'assistant', content: m.text };
    });

    const stepsRef = { current: [] };

    try {
      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages, model: selectedModel }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === 'thinking') {
            setLiveStatus(event.text);
          } else if (event.type === 'tool_call') {
            const step = { id: event.id, tool: event.tool, label: event.label, args: event.args };
            stepsRef.current = [...stepsRef.current, step];
            setLiveSteps([...stepsRef.current]);
            setLiveStatus('Working...');
          } else if (event.type === 'tool_result') {
            stepsRef.current = stepsRef.current.map(s =>
              s.id === event.id ? { ...s, result: event.result, error: event.error } : s
            );
            setLiveSteps([...stepsRef.current]);
          } else if (event.type === 'done') {
            setMessages(prev => [...prev, {
              sender: 'GrokCoder',
              text: event.content,
              steps: stepsRef.current,
            }]);
            setLiveSteps([]);
            setLiveStatus('');
            setLoading(false);
          } else if (event.type === 'error') {
            setMessages(prev => [...prev, { sender: 'GrokCoder', text: 'Error: ' + event.message }]);
            setLiveSteps([]);
            setLiveStatus('');
            setLoading(false);
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { sender: 'GrokCoder', text: 'Error: ' + err.message }]);
      setLiveSteps([]);
      setLiveStatus('');
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearHistory = () => {
    if (window.confirm('Clear chat history? This cannot be undone.')) {
      setMessages([initialMessage]);
      setPendingImage(null);
    }
  };

  const modelOptions = [
    { id: 'grok-4-1-fast-reasoning', name: 'Grok 4.1 Fast Reasoning' },
    { id: 'grok-beta', name: 'Grok Beta' },
    { id: 'grok-2-latest', name: 'Grok 2 Latest' },
  ];

  const selectedDisplay = modelOptions.find(opt => opt.id === selectedModel)?.name || selectedModel;
  const canSend = (input.trim() || pendingImage) && !loading;

  return (
    <>
      {showChangelog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setShowChangelog(false); }}>
          <div className="bg-[#1a1a1a]/95 backdrop-blur-xl border border-[#333]/70 rounded-3xl p-8 max-w-3xl max-h-[85vh] w-full mx-4 overflow-y-auto shadow-2xl relative max-md:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8 pb-6 border-b border-[#333]/50">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent flex-shrink-0">Changelog</h2>
              <button className="text-xl text-gray-400 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-all duration-200 ml-auto flex-shrink-0" onClick={() => setShowChangelog(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="space-y-6 text-gray-200 leading-relaxed">
              {changelogEntries.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No changelog entries yet.</p>
              ) : changelogEntries.map((entry, i) => (
                <div key={entry.version} className={i === 0 ? "bg-emerald-500/10 p-6 rounded-2xl border border-emerald-400/30" : "bg-black/20 p-6 rounded-2xl border border-[#444]/50"}>
                  <p className={`text-lg font-semibold mb-3 ${i === 0 ? 'text-emerald-300' : 'text-blue-300'}`}>{entry.version} <span className="text-xs font-normal text-gray-500 ml-2">{entry.date}</span></p>
                  <ul className="space-y-2 text-sm list-disc pl-5">
                    {entry.changes.map((change, j) => <li key={j} dangerouslySetInnerHTML={{ __html: change }} />)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setShowSettings(false)}>
          <div className="bg-[#1a1a1a]/95 backdrop-blur-xl border border-[#333]/70 rounded-3xl p-8 max-w-2xl max-h-[85vh] w-full mx-4 overflow-y-auto shadow-2xl relative max-md:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8 pb-6 border-b border-[#333]/50">
              <h2 className="text-3xl font-bold text-white">Settings</h2>
              <button className="text-xl text-gray-400 hover:text-white p-2 rounded-xl hover:bg-white/10 transition-all duration-200" onClick={() => setShowSettings(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="space-y-6"><BillingSection />
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Git Key</label>
                <textarea
                  value={gitKey}
                  onChange={(e) => setGitKey(e.target.value)}
                  className="w-full bg-[#111]/50 border border-[#333]/50 rounded-2xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400/50 focus:bg-[#111] transition-all resize-vertical min-h-[80px]"
                  placeholder="Enter your GitHub token or private key..."
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">SSH Key</label>
                <textarea
                  value={sshKey}
                  onChange={(e) => setSshKey(e.target.value)}
                  className="w-full bg-[#111]/50 border border-[#333]/50 rounded-2xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400/50 focus:bg-[#111] transition-all resize-vertical min-h-[120px]"
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;..."
                  rows={8}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Git Repo Path</label>
                  <input
                    type="text"
                    value={gitRepoPath}
                    onChange={(e) => setGitRepoPath(e.target.value)}
                    className="w-full bg-[#111]/50 border border-[#333]/50 rounded-2xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400/50 focus:bg-[#111] transition-all"
                    placeholder="/path/to/your/repo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">SSH Server Path</label>
                  <input
                    type="text"
                    value={sshServerPath}
                    onChange={(e) => setSshServerPath(e.target.value)}
                    className="w-full bg-[#111]/50 border border-[#333]/50 rounded-2xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-400/50 focus:bg-[#111] transition-all"
                    placeholder="user@server.com:/path/to/server/repo"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  className="px-6 py-2.5 bg-gray-700/50 hover:bg-gray-600/50 border border-gray-500/50 text-gray-300 rounded-2xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
                  onClick={() => setShowSettings(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-200 flex items-center gap-2"
                  onClick={saveSettings}
                >
                  <i className="fas fa-save"></i>
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col h-screen bg-[#0f0f0f] text-white font-sans antialiased selection:bg-blue-500/30">
        <header className="h-24 flex flex-col bg-[#0f0f0f]/98 backdrop-blur-md border-b border-[#1a1a1a]/50 shrink-0 shadow-sm relative z-10">
          <div className="h-10 flex items-center px-6 border-b border-[#1a1a1a]/30 bg-black/20 backdrop-blur-sm">
            <button
              className="text-xs font-mono tracking-wide bg-black/60 hover:bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-xl border border-white/30 text-gray-400 hover:text-emerald-300 hover:border-emerald-400/50 shadow-md transition-all duration-300 whitespace-nowrap flex items-center gap-1 group"
              onClick={() => setShowChangelog(true)}
            >
              <span>v{version}</span>
              <i className="fas fa-list text-xs opacity-0 group-hover:opacity-100 transition-opacity ml-0.5"></i>
            </button>
          </div>
          <div className="h-14 flex items-center px-6">
            <div className="flex-1 flex justify-center items-center gap-6 max-sm:gap-3">
              <div className="flex items-center gap-3 p-3 -m-3 rounded-xl hover:bg-white/10 transition-all duration-200 cursor-default select-none flex-shrink-0">
                <i className="fas fa-laptop-code text-2xl text-emerald-400 flex-shrink-0"></i>
                <span className="text-xl font-bold bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">GrokCoder</span>
              </div>
              <div className="relative w-40 max-md:w-32 max-sm:w-48 flex-shrink-0">
                <button
                  className="w-full flex items-center justify-between px-3 py-1.5 text-sm font-semibold text-white/90 bg-black/30 hover:bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 transition-all duration-200 min-h-[28px]"
                  onClick={() => setShowModelMenu(prev => !prev)}
                >
                  <span className="truncate">{selectedDisplay}</span>
                  <i className="fas fa-chevron-down text-xs ml-1.5" style={{ transform: showModelMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}></i>
                </button>
                {showModelMenu && (
                  <div className="absolute top-full left-0 w-full bg-[#111]/95 backdrop-blur-xl border border-[#2a2a2a]/50 rounded-2xl shadow-2xl z-50 overflow-hidden mt-1 max-h-48 overflow-y-auto">
                    {modelOptions.map(({ id, name }) => (
                      <button key={id} className="w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-[#2a2a2a]/50 transition-colors first:rounded-t-2xl last:rounded-b-2xl" onClick={() => { setSelectedModel(id); setShowModelMenu(false); }}>
                        <span className="truncate">{name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 ml-4 flex-shrink-0">
              <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/10 backdrop-blur rounded-2xl border border-white/10 transition-all duration-200 min-h-[44px] whitespace-nowrap" onClick={clearHistory}>
                Clear <i className="fas fa-trash text-xs ml-1"></i>
              </button>
              <button 
                className="flex items-center justify-center p-3 hover:bg-white/10 backdrop-blur rounded-2xl border border-white/10 transition-all duration-200 text-gray-400 hover:text-emerald-300 flex-shrink-0"
                onClick={() => window.location.reload()}
                title="Refresh"
              >
                <i className="fas fa-rotate text-xl"></i>
              </button>
              <button 
                className="flex items-center justify-center p-3 hover:bg-white/10 backdrop-blur rounded-2xl border border-white/10 transition-all duration-200 text-gray-400 hover:text-blue-300 flex-shrink-0"
                onClick={() => setShowSettings(true)}
                title="Settings"
              >
                <i className="fas fa-gear text-xl"></i>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 space-y-5 bg-[#0f0f0f] scroll-smooth">
          {messages.map((msg, i) => (
            <div key={i}>
              {msg.imagePreview && (
                <div className={msg.sender === 'You' ? 'flex justify-end mb-2' : 'flex justify-start mb-2'}>
                  <img src={msg.imagePreview} alt="Attached" className="max-h-48 rounded-2xl border border-white/10" />
                </div>
              )}
              <MessageBubble msg={msg} />
            </div>
          ))}
          {loading && liveSteps.length > 0 && (
            <ActivityBubble steps={liveSteps} statusText={liveStatus} />
          )}
          {loading && liveSteps.length === 0 && (
            <div className="flex justify-start">
              <div className="bg-[#1a1a1a]/80 backdrop-blur-sm px-6 py-4 rounded-3xl text-gray-400 italic text-base flex items-center gap-3 border border-[#333]/60">
                <span className="inline-block w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                {liveStatus || 'Thinking...'}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </main>

        <div className="shrink-0 p-4 pt-0 bg-[#0f0f0f]/98 backdrop-blur-sm">
          {pendingImage && (
            <div className="max-w-4xl mx-auto mb-2 flex items-center gap-2 px-2">
              <img src={pendingImage.previewUrl} alt="Pending" className="h-16 w-16 object-cover rounded-xl border border-white/20" />
              <button className="text-gray-400 hover:text-white text-sm" onClick={() => setPendingImage(null)}>
                <i className="fas fa-times"></i> Remove
              </button>
            </div>
          )}
          <div className="bg-[#121212]/90 hover:bg-[#121212] border border-[#2a2a2a]/60 hover:border-[#3a3a3a]/70 rounded-3xl p-4 flex flex-col gap-4 transition-all duration-200 max-w-4xl mx-auto">
            <div className="flex items-stretch gap-3">
              <textarea
                ref={textAreaRef}
                className="flex-1 bg-transparent border-none outline-none resize-none text-white placeholder-gray-500 text-base leading-relaxed py-4 px-4 overflow-hidden min-h-[44px] max-h-[120px]"
                value={input}
                onChange={(e) => { setInput(e.target.value); adjustHeight(); }}
                onKeyDown={handleKeyDown}
                placeholder="Message GrokCoder..."
                disabled={loading}
              />
              <button
                className={`self-stretch flex items-center justify-center p-3 rounded-2xl transition-all duration-200 shadow-lg flex-shrink-0 ${canSend ? 'bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 text-white shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-[1.05] active:scale-[0.98]' : 'bg-gray-700/50 text-gray-500 cursor-not-allowed shadow-none'}`}
                onClick={sendMessage}
                disabled={!canSend}
              >
                <i className="fas fa-paper-plane text-lg"></i>
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 pt-3 border-t border-[#2a2a2a]/40 text-gray-500 text-sm flex-wrap">
              <button className="flex items-center justify-center p-3 text-gray-500 hover:text-gray-300 hover:bg-white/10 rounded-2xl transition-all duration-200 flex-shrink-0">
                <i className="fas fa-microphone text-xl"></i>
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleImageSelect} />
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-2xl transition-all duration-200 ${pendingImage ? 'text-emerald-400 bg-emerald-500/10' : 'text-gray-500 hover:text-gray-300 hover:bg-white/10'}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-image text-lg"></i>
                <span className="text-sm font-medium">{pendingImage ? 'Image ready' : 'Image'}</span>
              </button>
              <div className="flex items-center gap-2 text-gray-600">
                <i className="fas fa-music p-1"></i>
                <i className="fas fa-mobile-alt p-1"></i>
                <i className="fas fa-plug p-1"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default App;